import React from "react";
import { Box, Typography, Paper, LinearProgress, Chip } from "@mui/material";
import MemoryIcon from "@mui/icons-material/Memory";
import StorageIcon from "@mui/icons-material/Storage";
import SpeedIcon from "@mui/icons-material/Speed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";
import ComputerIcon from "@mui/icons-material/Computer";
import "./VpsStats.css";

export default function VpsStats({ vpsStats }) {
    if (!vpsStats || vpsStats.error) {
        return (
            <Box className="vps-stats-box">
                <Typography variant="h6" className="vps-stats-title">
                    VPS Statistikaları
                </Typography>
                <Typography variant="body2" color="error">
                    {vpsStats?.error || "Məlumat tapılmadı"}
                </Typography>
            </Box>
        );
    }

    const { cpu, memory, disk, os, uptime, network } = vpsStats;

    const getUsageColor = (percent) => {
        if (percent < 50) return "#4caf50";
        if (percent < 75) return "#ff9800";
        return "#f44336";
    };

    return (
        <Box className="vps-stats-box">
            <Box className="vps-stats-header">
                <Typography variant="h6" className="vps-stats-title">
                    VPS Statistikaları
                </Typography>
                <Chip 
                    icon={<AccessTimeIcon />} 
                    label={uptime?.formatted || "Bilinmir"} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                />
            </Box>

            <Box className="vps-stats-grid">
                {/* CPU */}
                <Box className="vps-stat-item">
                    <Paper className="vps-stat-card">
                        <Box className="vps-stat-content">
                            <Box className="vps-stat-header">
                                <SpeedIcon className="vps-stat-icon cpu-icon" />
                                <Typography variant="subtitle2" className="vps-stat-label">
                                    CPU
                                </Typography>
                            </Box>
                            <Typography variant="h6" className="vps-stat-value">
                                {cpu?.currentLoad || 0}%
                            </Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={parseFloat(cpu?.currentLoad || 0)} 
                                className="vps-stat-progress"
                                sx={{ 
                                    '& .MuiLinearProgress-bar': { 
                                        backgroundColor: getUsageColor(parseFloat(cpu?.currentLoad || 0)) 
                                    } 
                                }}
                            />
                            <Typography variant="caption" className="vps-stat-detail">
                                {cpu?.cores || 0} Core • {cpu?.brand || "Bilinmir"}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>

                {/* Memory */}
                <Box className="vps-stat-item">
                    <Paper className="vps-stat-card">
                        <Box className="vps-stat-content">
                            <Box className="vps-stat-header">
                                <MemoryIcon className="vps-stat-icon memory-icon" />
                                <Typography variant="subtitle2" className="vps-stat-label">
                                    RAM
                                </Typography>
                            </Box>
                            <Typography variant="h6" className="vps-stat-value">
                                {memory?.usagePercent || 0}%
                            </Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={parseFloat(memory?.usagePercent || 0)} 
                                className="vps-stat-progress"
                                sx={{ 
                                    '& .MuiLinearProgress-bar': { 
                                        backgroundColor: getUsageColor(parseFloat(memory?.usagePercent || 0)) 
                                    } 
                                }}
                            />
                            <Typography variant="caption" className="vps-stat-detail">
                                {memory?.active || 0}GB / {memory?.total || 0}GB
                            </Typography>
                        </Box>
                    </Paper>
                </Box>

                {/* Disk */}
                <Box className="vps-stat-item">
                    <Paper className="vps-stat-card">
                        <Box className="vps-stat-content">
                            <Box className="vps-stat-header">
                                <StorageIcon className="vps-stat-icon disk-icon" />
                                <Typography variant="subtitle2" className="vps-stat-label">
                                    Disk
                                </Typography>
                            </Box>
                            {disk && disk.length > 0 && disk[0] && (
                                <>
                                    <Typography variant="h6" className="vps-stat-value">
                                        {disk[0].usePercent || 0}%
                                    </Typography>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={parseFloat(disk[0].usePercent || 0)} 
                                        className="vps-stat-progress"
                                        sx={{ 
                                            '& .MuiLinearProgress-bar': { 
                                                backgroundColor: getUsageColor(parseFloat(disk[0].usePercent || 0)) 
                                            } 
                                        }}
                                    />
                                    <Typography variant="caption" className="vps-stat-detail">
                                        {disk[0].used || 0}GB / {disk[0].size || 0}GB
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Paper>
                </Box>

                {/* OS Info */}
                <Box className="vps-stat-item">
                    <Paper className="vps-stat-card">
                        <Box className="vps-stat-content">
                            <Box className="vps-stat-header">
                                <ComputerIcon className="vps-stat-icon os-icon" />
                                <Typography variant="subtitle2" className="vps-stat-label">
                                    Sistem
                                </Typography>
                            </Box>
                            <Typography variant="body2" className="vps-stat-value">
                                {os?.distro || "Bilinmir"} {os?.release || ""}
                            </Typography>
                            <Typography variant="caption" className="vps-stat-detail">
                                {os?.arch || ""} • {os?.hostname || ""}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>

                {/* Network */}
                {network && network.length > 0 && (
                    <Box className="vps-stat-item">
                        <Paper className="vps-stat-card network-card">
                            <Box className="vps-stat-content">
                                <Box className="vps-stat-header">
                                    <NetworkCheckIcon className="vps-stat-icon network-icon" />
                                    <Typography variant="subtitle2" className="vps-stat-label">
                                        Şəbəkə
                                    </Typography>
                                </Box>
                                <Box className="vps-network-details">
                                    {network.filter(n => !n.internal).map((iface, idx) => (
                                        <Typography key={idx} variant="caption" className="vps-stat-detail">
                                            <strong>{iface.iface}:</strong> {iface.ip4 || "No IPv4"}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Box>
        </Box>
    );
}